import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from './core/guard/auth.guard';
import { ModuleGuard } from './core/guard/module.guard';
import { PracticeGuard } from './core/guard/practice.guard';
import { MODULES } from './core/config/module-codes';
import { AuthLayoutComponent } from './layout/auth-layout/auth-layout.component';
import { RegistrationLayoutComponent } from './layout/registration-layout/registration-layout.component';
import { ContentLayoutComponent } from './layout/content-layout/content-layout.component';
import { SignupLayoutComponent } from './layout/signup-layout/signup-layout.component';
import { AppleAppSiteAssociationComponent } from './AppleAppSiteAssociationComponent';

const routes: Routes = [
  {
    path: '',
    redirectTo: '/auth/login',
    pathMatch: 'full'
  },
  {
    path: 'auth',
    component: AuthLayoutComponent,
    loadChildren: () => import('./modules/auth/auth.module').then(m => m.AuthModule)
  },
  {
    /// this will be not in used after we complete work on SD-198
    path: 'register',
    // canActivate: [NoAuthGuard], // Should be replaced with actual auth guard
    component: RegistrationLayoutComponent,
    loadChildren: () => import('./modules/register/register.module').then(m => m.RegisterModule)
  },
  {
    path: 'signup',
    component: SignupLayoutComponent,
    loadChildren: () => import('./modules/signup/signup.module').then(m => m.SignUpModule)
  },
  {
    path: 'signed-in-redirect',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    // SD-1137: signup terminates here. Incomplete onboarding surfaces via the
    // OnboardingChecklistCard on the dashboard, not via a guard that kicks users back.
    loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule)
  },
  {
    // SD-1252: the practice's Emails view: the Emails page in practice mode, across every client
    // the member looks after. Its own route because EmailsModule is lazy and owns the component;
    // no ModuleGuard, which reads the selected organisation (here there is none).
    path: 'clients/emails',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, PracticeGuard],
    canActivateChild: [AuthGuard, PracticeGuard],
    loadChildren: () => import('./modules/emails/emails.module').then(m => m.EmailsModule)
  },
  {
    // SD-1248: an advisor practice's All clients page and its Team page. PracticeGuard: no
    // practice -> Home; its canActivateChild also keeps advisors off the managers-only Team page.
    path: 'clients',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, PracticeGuard],
    canActivateChild: [AuthGuard, PracticeGuard],
    loadChildren: () => import('./modules/clients/clients.module').then(m => m.ClientsModule)
  },
  {
    // SD-1248: the invitation email's link. Public: signed in, it goes on to /practice-invite;
    // signed out, someone invited to a practice's team (or to own a client) with no account
    // creates one here -- no organisation of their own -- and others sign in.
    path: 'practice-join',
    component: AuthLayoutComponent,
    loadChildren: () => import('./modules/clients/practice-join/practice-join.module').then(m => m.PracticeJoinModule)
  },
  {
    // SD-1248: where a practice invitation's email link lands. Any signed-in user -- the people
    // invited are usually not in a practice -- so AuthGuard only. Signed out, AuthGuard sends
    // them to log in with ?redirectURL= and they come back here.
    path: 'practice-invite',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/clients/practice-invite/practice-invite.module').then(m => m.PracticeInviteModule)
  },
  {
    path: 'report',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/report/report.module').then(m => m.ReportModule)
  },
  {
    path: 'planning',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/planning/planning.module').then(m => m.PlanningModule)
  },
  {
    path: 'emails',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.EMAILS },
    loadChildren: () => import('./modules/emails/emails.module').then(m => m.EmailsModule)
  },
  {
    path: 'transactions',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.TRANSACTIONS },
    loadChildren: () => import('./modules/transactions/transactions.module').then(m => m.TransactionsModule)
  },
  {
    path: 'paperwork',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.PAPERWORK },
    loadChildren: () => import('./modules/paperwork/paperwork.module').then(m => m.PaperworkModule)
  },
  {
    path: 'automate',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.FLOWS },
    loadChildren: () => import('./modules/automate/automate.module').then(m => m.AutomateModule)
  },
  {
    path: 'agents',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.AGENTS },
    loadChildren: () => import('./modules/agents/agents.module').then(m => m.AgentsModule)
  },
  {
    path: 'payable',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.BILLS_TO_PAY },
    loadChildren: () => import('./modules/payable/payable.module').then(m => m.PayableModule)
  },
  {
    path: 'suppliers',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/suppliers/suppliers.module').then(m => m.SuppliersModule)
  },
  {
    path: 'invoices',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.INVOICES },
    loadChildren: () => import('./modules/invoices/invoices.module').then(m => m.InvoicesModule)
  },
  {
    // SD-1249: the Timesheet. Each page also needs time.log / time.settings (PermissionGuard).
    path: 'time',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.TIME },
    loadChildren: () => import('./modules/time/time.module').then(m => m.TimeModule)
  },
  {
    path: 'inbox',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard, ModuleGuard],
    canActivateChild: [AuthGuard, ModuleGuard],
    data: { requiredModule: MODULES.INBOX },
    loadChildren: () => import('./modules/bookkeeping/bookkeeping.module').then(m => m.BookkeepingModule)
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./modules/onboarding/onboarding.module').then(m => m.OnboardingModule)
  },
  {
    // The admin portal is the separate Synced Admin app now. /admin keeps only the CMS visual
    // editor (signed in, super admins) and sends every other address there, signed in or not,
    // so its guards live in AdminRoutingModule.
    path: 'admin',
    loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule)
  },
  {
    path: 'setting',
    component: ContentLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/setting/setting.module').then(m => m.SettingModule)
  },
  {
    path: 'support',
    redirectTo: '/support',
    pathMatch: 'full'
  },
  {
    // SD-1234: the inbox assistant on its own, for the Chrome extension's Gmail overlay.
    // No layout or guard; see src/app/shared/inbox-assistant/README.md.
    path: 'assistant-frame',
    loadChildren: () => import('./shared/inbox-assistant/frame/inbox-assistant-frame.module').then(m => m.InboxAssistantFrameModule)
  },
  {
    path: '.well-known/apple-app-site-association',
    component: AppleAppSiteAssociationComponent
  },
  // Redirect signed in user to the '/dashboards/tasks'
  // Fallback when no prior routes is matched
  {
    path: '**',
    redirectTo: '/auth/login',
    pathMatch: 'full'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes
    )
  ],
  exports: [RouterModule],
  providers: []
})
export class AppRoutingModule { }
